/**
 * Sammelsendung (Task 4b): der Bestaetigungsschritt, der `ReichweiteAuswahl.tsx` beim Hochladen
 * tatsaechlich einblendet - ohne diesen Dialog bliebe die Auswahl-Komponente ein isoliert getestetes,
 * aber nirgends erreichbares Bauteil. Erscheint fuer JEDEN Upload (Checkliste, "Sonstige Dokumente",
 * Bestellexcel, PI-Import - alle laufen durch `DokumenteTab.tsx#dateiGewaehlt`), aber NUR, wenn die
 * Bestellung an einer Sendung haengt (`bestellung.dsvShipmentNo`): ohne Sendung gibt es nichts zu
 * waehlen, `ReichweiteAuswahl` zeigt dann ohnehin nur eine einzige, fest gewaehlte Option.
 *
 * Gleiches Baumuster wie `UeberschreibDialog.tsx` (`createPortal` nach `document.body`, `role=
 * "dialog"`, dieselbe Kopf-/Fuss-Gliederung `board-editor-*`) - siehe dessen Kopfkommentar fuer die
 * Begruendung des Portals. Anders als dort gibt es hier keine Pflichteingabe: die Vorbelegung ist
 * bereits eine gueltige Wahl, "Weiter" ist von Anfang an bedienbar.
 */
import { createPortal } from "react-dom";
import { useState } from "react";
import { ReichweiteAuswahl } from "./ReichweiteAuswahl.tsx";
import type { Reichweite } from "../../../lib/dokumentReichweite.ts";
import { reichweiteVorgabe } from "../../../lib/dokumentReichweite.ts";
import { Ikone } from "../../Ikone.tsx";

export interface ReichweiteDialogProps {
  /** Beschriftung des Dokumenttyps (`docType.label`), wie `UeberschreibDialog.typLabel`. */
  typLabel: string;
  docType: string;
  dsvShipmentNo?: string | null;
  orderGroupId?: string | null;
  orderGroupMemberCount?: number | null;
  orderGroupStatus?: "active" | "dissolved" | null;
  onAbbrechen: () => void;
  onBestaetigen: (reichweite: Reichweite) => void;
}

export function ReichweiteDialog({
  typLabel,
  docType,
  dsvShipmentNo,
  orderGroupId,
  orderGroupMemberCount,
  orderGroupStatus,
  onAbbrechen,
  onBestaetigen,
}: ReichweiteDialogProps) {
  const [gewaehlt, setGewaehlt] = useState<Reichweite>(() =>
    dsvShipmentNo ? reichweiteVorgabe(docType) : "bestellung",
  );

  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section
        className="board-editor-dialog reichweite-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reichweiteDialogTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="reichweiteDialogTitel">Reichweite wählen</h2>
            <p>{typLabel} kann für diese Bestellung allein, ihre aktive Bestellgruppe oder ihre Sendung gelten.</p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <ReichweiteAuswahl
            docType={docType}
            dsvShipmentNo={dsvShipmentNo}
            orderGroupId={orderGroupId}
            orderGroupMemberCount={orderGroupMemberCount}
            orderGroupStatus={orderGroupStatus}
            onChange={setGewaehlt}
          />
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={() => onBestaetigen(gewaehlt)}>
            <Ikone name="upload" />
            Weiter
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
